import { useState, useEffect } from 'react';
import { useApp } from '@/context/AppContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { MenuItem, WeekMenu } from '@/lib/lunchData';
import { Save, Eye, ArrowLeft, Leaf } from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { WeekInfo, getWeeksForCMS, isSameWeek } from '@/lib/dateUtils';
import { useCompanyRequests } from '@/hooks/useCompanyRequests';

const days = ['Måndag', 'Tisdag', 'Onsdag', 'Torsdag', 'Fredag'];

const PersonalMenuCMS = () => {
  const { menus, updateMenu, currentWeekInfo } = useApp();
  const [selectedWeek, setSelectedWeek] = useState<WeekInfo>(currentWeekInfo);
  const [showPreview, setShowPreview] = useState(false);
  
  // Hämtar företagsförfrågningar för notificering i menyraden
  const { data: requests = [] } = useCompanyRequests();
  const unhandledCount = requests.filter(r => r.status !== 'handled').length;

  const existingMenu = menus.find(m => m.week === selectedWeek.week && m.year === selectedWeek.year);
  
  const [items, setItems] = useState<MenuItem[]>(
    existingMenu?.items || days.map((day, i) => ({
      id: `new-${i}`,
      day,
      dish: '',
      dish2: '',
      vegetarian: '',
      closed: false,
      comment: '',
    }))
  );

  const weeksToShow = getWeeksForCMS();

  const handleItemChange = (index: number, field: keyof MenuItem, value: string | boolean) => {
    setItems(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const handleSave = () => {
    const filledItems = items.filter(item => item.dish.trim() !== '');
    
    if (filledItems.length === 0) {
      toast.error('Lägg till minst en maträtt');
      return;
    }

    const menu: WeekMenu = {
      week: selectedWeek.week,
      year: selectedWeek.year,
      items: items.map((item, i) => ({
        ...item,
        id: item.id.startsWith('new-') ? `item-${selectedWeek.year}-${selectedWeek.week}-${i}` : item.id,
      })),
    };

    updateMenu(menu);
    toast.success(`Vecka ${selectedWeek.week}, ${selectedWeek.year} sparad!`);
  };

  const loadWeek = (weekInfo: WeekInfo) => {
    setSelectedWeek(weekInfo);
    const menu = menus.find(m => m.week === weekInfo.week && m.year === weekInfo.year);
    if (menu) {
      setItems(menu.items);
    } else {
      setItems(days.map((day, i) => ({
        id: `new-${i}`,
        day,
        dish: '',
        dish2: '',
        vegetarian: '',
        closed: false,
        comment: '',
      })));
    }
  };

  // Uppdatera items när selectedWeek ändras
  useEffect(() => {
    const menu = menus.find(m => m.week === selectedWeek.week && m.year === selectedWeek.year);
    if (menu) {
      setItems(menu.items);
    } else {
      setItems(days.map((day, i) => ({
        id: `new-${i}`,
        day,
        dish: '',
        dish2: '',
        vegetarian: '',
        closed: false,
        comment: '',
      })));
    }
  }, [selectedWeek, menus]);

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-background border-b border-border">
        <div className="container-wide mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="text-muted-foreground hover:text-foreground transition-colors">
              <ArrowLeft size={20} />
            </Link>
            <h1 className="font-display text-xl font-semibold text-foreground">
              Personalvy – Meny
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              onClick={() => setShowPreview(!showPreview)}
            >
              <Eye size={20} />
              {showPreview ? 'Dölj förhandsgranskning' : 'Förhandsgranska'}
            </Button>
            <Link to="/personal/forfragningar">
              <Button variant="ghost" className="relative">
                Förfrågningar
                {unhandledCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-destructive text-destructive-foreground text-[10px] font-bold px-1.5 py-0.5 rounded-full flex items-center justify-center min-w-[18px]">
                    {unhandledCount}
                  </span>
                )}
              </Button>
            </Link>
            <Link to="/personal/kassa">
              <Button variant="secondary">Kassa</Button>
            </Link>
          </div>
        </div>
      </header>

      <main className="container-wide mx-auto px-4 py-8">
        <div className={cn("grid gap-8", showPreview && "lg:grid-cols-2")}>
          {/* Editor */}
          <div>
            {/* Week selector */}
            <div className="mb-6 flex flex-wrap items-center gap-3">
              <span className="text-sm font-medium text-foreground">Vecka:</span>
              {weeksToShow.map(weekInfo => (
                <button
                  key={`${weekInfo.year}-${weekInfo.week}`}
                  onClick={() => loadWeek(weekInfo)}
                  className={cn(
                    "px-4 py-2 rounded-lg text-sm font-medium transition-all",
                    isSameWeek(selectedWeek, weekInfo)
                      ? "bg-primary text-primary-foreground"
                      : "bg-secondary text-secondary-foreground hover:bg-secondary/80"
                  )}
                >
                  V.{weekInfo.week}
                  {isSameWeek(weekInfo, currentWeekInfo) && ' (denna)'}
                </button>
              ))}
            </div>

            <p className="text-sm text-muted-foreground mb-4">
              Redigerar: Vecka {selectedWeek.week}, {selectedWeek.year}
            </p>

            {/* Menu items */}
            <div className="space-y-4">
              {items.map((item, index) => (
                <div key={item.id} className="card-warm p-6">
                  <div className="flex items-center justify-between mb-4">
                    <span className="px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium">
                      {item.day}
                    </span>
                    
                    <label className="flex items-center gap-2 cursor-pointer">
                      <span className="text-sm font-medium text-foreground">Stängt</span>
                      <input
                        type="checkbox"
                        checked={item.closed || false}
                        onChange={(e) => handleItemChange(index, 'closed', e.target.checked)}
                        className="w-4 h-4 rounded border-gray-300 text-primary focus:ring-primary"
                      />
                    </label>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1">
                        {item.closed ? 'Anledning (visas som huvudrätt)' : 'Maträtt 1'}
                      </label>
                      <Input
                        value={item.dish}
                        onChange={(e) => handleItemChange(index, 'dish', e.target.value)}
                        placeholder="T.ex. Köttbullar med potatismos"
                      />
                    </div>

                    {!item.closed && (
                      <div className="animate-fade-in-up">
                        <label className="block text-sm font-medium text-foreground mb-1">
                          Maträtt 2 (valfritt)
                        </label>
                        <Input
                          value={item.dish2 || ''}
                          onChange={(e) => handleItemChange(index, 'dish2', e.target.value)}
                          placeholder="T.ex. Alternativ rätt"
                        />
                      </div>
                    )}

                    {!item.closed && (
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1">
                        Vegetariskt alternativ
                      </label>
                      <Input
                        value={item.vegetarian}
                        onChange={(e) => handleItemChange(index, 'vegetarian', e.target.value)}
                        placeholder="T.ex. Vegetariska biffar med potatismos"
                      />
                    </div>
                    )}

                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1">
                        Kommentar (valfritt)
                      </label>
                      <Input
                        value={item.comment || ''}
                        onChange={(e) => handleItemChange(index, 'comment', e.target.value)}
                        placeholder="T.ex. Serveras med lingon"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-6">
              <Button onClick={handleSave} size="lg">
                <Save size={18} />
                Spara vecka {selectedWeek.week}, {selectedWeek.year}
              </Button>
            </div>
          </div>

          {/* Preview */}
          {showPreview && (
            <div className="lg:sticky lg:top-24 lg:self-start">
              <div className="card-warm p-6">
                <h2 className="font-display text-lg font-semibold text-foreground mb-4">
                  Förhandsgranskning – Vecka {selectedWeek.week}, {selectedWeek.year}
                </h2>
                <div className="space-y-4">
                  {items.filter(item => item.dish).map((item) => (
                    <div key={item.id} className="p-4 rounded-lg bg-secondary/50">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-primary">{item.day}</span>
                        {item.comment && (
                          <span className="text-xs text-muted-foreground">{item.comment}</span>
                        )}
                      </div>
                      
                      {item.closed ? (
                        <div className="py-2 flex justify-center">
                          <span className="inline-block px-3 py-1 bg-red-100 text-red-800 rounded-full text-sm font-medium">
                            {item.dish || 'Stängt'}
                          </span>
                        </div>
                      ) : (
                        <>
                          <p className="font-medium text-foreground mb-1">{item.dish}</p>
                          {item.dish2 && (
                            <p className="font-medium text-foreground mb-2 pt-1 border-t border-border/50">
                              {item.dish2}
                            </p>
                          )}
                          <div className="flex items-center gap-2 text-sm text-vegetarian mt-2">
                            {item.vegetarian && <Leaf size={14} />}
                            {item.vegetarian}
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                  {items.filter(item => item.dish).length === 0 && (
                    <p className="text-muted-foreground text-center py-8">
                      Lägg till maträtter för att se förhandsgranskningen
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default PersonalMenuCMS;
